"use client";

import MediaImage from "@/components/ui/MediaImage";

import React, { useEffect, useState } from "react";
import { InstagramPost } from "@/services/queries/get-instagram-posts";
import { getInstagramPostsAction } from "@/actions/get-instagram-posts";
import {
  getMargins,
  getPaddings,
  getRounded,
  getTextSize,
  ownRadiusStyle,
} from "@/utils/theme-helper";
import { cornerLengths } from "@/utils/form-values";
import { isLookSystem } from "@/utils/look-gate";
import { SpinnerSvg } from "../icons/Spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAlbumCollection } from "@fortawesome/sharp-light-svg-icons";
import { faVideo, faAlbum } from "@fortawesome/pro-light-svg-icons";
import { RealequityVideoComponent } from "./RealequityVideoComponent";

export interface InstagramPostsComponentProps {
  config: {
    numberOfPosts: number;
    description?: string;
    followButtonText?: string;
    instagramHandle?: string;
    resourceGroupId?: string;
    applicationId?: string;
    mobileColumns?: 1 | 2 | 3 | 4;
    margin?: string;
    padding?: string;
    rounded?: string;
    size?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "4xl";
    disableAnimation?: boolean;
    disableCaptions?: boolean;
    textColor?: string;
    bgColor?: string;
    borderColor?: string;
    iconColor?: string;
    iconSize?: "xs" | "sm" | "md" | "lg" | "xl";
    postRounded?: "none" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";
    height?: string;
    captionBgColor?: string;
    cmsData?: { theme?: unknown };
  };
  id?: string;
  itemIndex?: number;
  applicationId?: string;
  initialPosts?: InstagramPost[];
}

const InstagramPostsComponent: React.FC<InstagramPostsComponentProps> = ({
  config,
  applicationId,
  initialPosts,
}) => {
  // Default configuration
  const defaultConfig = {
    numberOfPosts: 5,
    description: "",
    followButtonText: "Følg os på Instagram",
    instagramHandle: "realequity",
    mobileColumns: 2,
    margin: "none",
    padding: "none",
    rounded: "none",
    size: "md",
    disableAnimation: false,
    disableCaptions: false,
    textColor: "var(--base-text-color)",
    bgColor: "transparent",
    borderColor: "transparent",
    iconColor: "#ffffff",
    iconSize: "md",
    postRounded: "lg",
    height: "auto",
    captionBgColor: "rgba(0, 0, 0, 0.8)",
  };

  const finalConfig = { ...defaultConfig, ...config };
  const lookSystem = isLookSystem(config.cmsData?.theme);

  const textSizeClass =
    finalConfig.size && finalConfig.size !== "none"
      ? `text-${finalConfig.size}`
      : "text-sm";

  const normalizeText = (text: string) => {
    return text.normalize("NFKC"); // Normalize to composed form, compatibility mode
  };

  const getIconSizeClass = (size: string) => {
    switch (size) {
      case "xs":
        return "w-3 h-3";
      case "sm":
        return "w-4 h-4";
      case "md":
        return "w-6 h-6";
      case "lg":
        return "w-8 h-8";
      case "xl":
        return "w-12 h-12";
      default:
        return "w-6 h-6";
    }
  };

  const getIconContainerSizeClass = (size: string) => {
    switch (size) {
      case "xs":
        return "w-5 h-5";
      case "sm":
        return "w-6 h-6";
      case "md":
        return "w-8 h-8";
      case "lg":
        return "w-10 h-10";
      case "xl":
        return "w-15 h-15";
      default:
        return "w-8 h-8";
    }
  };

  const iconSizeClass = getIconSizeClass(finalConfig.iconSize || "md");
  const iconContainerSizeClass = getIconContainerSizeClass(
    finalConfig.iconSize || "md",
  );

  // Map post rounded corners to Tailwind classes
  const getPostRoundedClass = (rounded: string) => {
    if (rounded === "none") return "rounded-none";
    return `rounded-${rounded}`;
  };

  const postRounded = finalConfig.postRounded || "lg";
  const exactPost = lookSystem && !!cornerLengths(postRounded);
  const postRoundedClass = exactPost
    ? getRounded(postRounded, true)
    : getPostRoundedClass(postRounded);

  const gridContainerClass = `w-full ${finalConfig.padding ? getPaddings(finalConfig.padding) : ""} bg-[var(--instagram-bg-color)] border-[var(--instagram-border-color)]`;

  const mainContainerClass = `flex ${getTextSize(finalConfig?.size, "sm")} ${getMargins(finalConfig.margin)} ${getRounded(finalConfig.rounded, lookSystem)}`;

  const finalApplicationId = applicationId || config?.applicationId;
  // State for Instagram posts
  const [posts, setPosts] = useState<InstagramPost[]>(initialPosts ?? []);
  const [loading, setLoading] = useState(initialPosts === undefined);
  const [error, setError] = useState<string | null>(null);

  // Fetch Instagram posts
  useEffect(() => {
    if (initialPosts !== undefined) {
      setPosts(initialPosts);
      setLoading(false);
      return;
    }
    const fetchPosts = async () => {
      if (!finalApplicationId || !finalConfig.resourceGroupId) {
        console.warn("Missing required parameters for Instagram posts:", {
          applicationId: finalApplicationId,
          resourceGroupId: finalConfig.resourceGroupId,
        });
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await getInstagramPostsAction(
          finalApplicationId,
          finalConfig.resourceGroupId,
          finalConfig.numberOfPosts,
        );

        if (response.success && response.data?.data) {
          setPosts(response.data.data);
        } else {
          console.error("Failed to fetch Instagram posts:", response);
          setError(response.error || "No Instagram posts found");
        }
      } catch (err) {
        console.error("Error fetching Instagram posts:", err);
        setError("Failed to load Instagram posts");
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [
    finalApplicationId,
    finalConfig.resourceGroupId,
    finalConfig.numberOfPosts,
    initialPosts,
  ]);

  const actualPostCount = posts.length;

  // Calculate responsive grid sizing based on number of posts
  const getGridConfig = () => {
    // Use configurable height
    const heightClass =
      finalConfig.height === "auto" ? "h-80" : `h-[${finalConfig.height}]`;

    // Responsive grid - mobile columns setting, then all posts in one row on desktop
    // Use max-md for mobile, lg+ for desktop (avoid md: to prevent conflicts)

    const mobileCols = finalConfig.mobileColumns || 2;
    const cols = `grid-cols-${mobileCols} md:grid-cols-${finalConfig.numberOfPosts}`;

    const itemSize = `w-full ${heightClass}`;

    return { cols, itemSize };
  };

  const gridConfig = getGridConfig();

  const renderInstagramPost = (post: InstagramPost, index: number) => (
    <div key={post.id} className={`relative ${gridConfig.itemSize} mx-auto`}>
      <a
        href={post.permalink || "#"}
        target="_blank"
        rel="noopener noreferrer"
        className={`group block w-full h-full overflow-hidden ${postRoundedClass} hover:shadow-lg bg-[var(--instagram-bg-color)] ${!finalConfig.disableAnimation ? "transition-all duration-300" : ""}`}
        style={exactPost ? ownRadiusStyle(postRounded, true) : undefined}
      >
        {/*
          Instagram returns mediaType VIDEO with mediaUrl === null for some
          Reels, while still providing thumbnailUrl. Rendering the video in that
          case produced <source src=""> (networkState 3, NETWORK_NO_SOURCE), so
          the tile stayed blank white and never loaded anything. Fall back to the
          thumbnail; the video icon overlay and the permalink still mark it as a
          video, so the tile keeps working as a link to the Reel.
        */}
        {post.mediaType === "VIDEO" && post.mediaUrl ? (
          <RealequityVideoComponent
            url={post.mediaUrl}
            poster={post.thumbnailUrl}
            className={`w-full h-full object-cover ${!finalConfig.disableAnimation ? "group-hover:scale-105 transition-transform duration-300" : ""}`}
            muted={true}
          />
        ) : (
          <MediaImage
            src={post.mediaUrl || post.thumbnailUrl || ""}
            alt={post.caption || `Instagram post ${index + 1}`}
            className={`w-full h-full object-cover ${!finalConfig.disableAnimation ? "group-hover:scale-105 transition-transform duration-300" : ""}`}
            loading="lazy"
          />
        )}

        {/* Video Play Icon */}
        {post.mediaType === "VIDEO" && (
          <div className="absolute top-2 right-2">
            <div
              className={`${iconContainerSizeClass} rounded-sm flex items-center justify-center shadow-sm`}
            >
              <FontAwesomeIcon
                className={`${iconSizeClass} text-[var(--instagram-icon-color)] ml-0.5`}
                icon={faVideo}
              />
            </div>
          </div>
        )}

        {/* Image Icon for IMAGE posts */}
        {post.mediaType === "IMAGE" && (
          <div className="absolute top-2 right-2">
            <div
              className={`${iconContainerSizeClass} rounded-sm flex items-center justify-center shadow-sm`}
            >
              <FontAwesomeIcon
                className={`${iconSizeClass} text-[var(--instagram-icon-color)]`}
                icon={faAlbum}
              />
            </div>
          </div>
        )}

        {/* Carousel Icon for CAROUSEL_ALBUM posts */}
        {post.mediaType === "CAROUSEL_ALBUM" && (
          <div className="absolute top-2 right-2">
            <div
              className={`${iconContainerSizeClass} rounded-sm flex items-center justify-center shadow-sm`}
            >
              <FontAwesomeIcon
                className={`${iconSizeClass} text-[var(--instagram-icon-color)]`}
                icon={faAlbumCollection}
              />
            </div>
          </div>
        )}

        {/* Hover overlay - only shows when animation enabled */}
        {!finalConfig.disableAnimation && (
          <div
            className="absolute inset-0 flex items-center justify-center p-4 opacity-0 group-hover:opacity-100 transition-all duration-300"
            style={{
              backgroundColor: "var(--instagram-caption-bg-color)",
            }}
          >
            {post.caption && !finalConfig.disableCaptions && (
              <p
                className={`text-[var(--instagram-text-color)] ${textSizeClass} text-center line-clamp-3 rounded-md`}
                style={{
                  fontFamily: "var(--font-family) !important",
                }}
              >
                {normalizeText(post.caption)}
              </p>
            )}
          </div>
        )}
      </a>
    </div>
  );
  if (loading) {
    return (
      <div
        className="w-full"
        data-type="InstagramPostsComponent"
        data-posts-count={0}
        data-loading={true}
      >
        <div className="flex justify-center items-center py-8">
          <div className="flex flex-col items-center">
            <SpinnerSvg />
            <p className="text-gray-600 mt-4">Indlæser instagram posts...</p>
          </div>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div
        className="w-full"
        data-type="InstagramPostsComponent"
        data-posts-count={0}
        data-error={true}
      >
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-4">⚠️</div>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // Handle edge cases for post count
  if (actualPostCount === 0) {
    return (
      <div
        className="w-full"
        data-type="InstagramPostsComponent"
        data-posts-count={0}
      >
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-4">📷</div>
          <p className="text-gray-600">No Instagram posts available</p>
        </div>
      </div>
    );
  }

  // Grid mode - return the original grid layout
  return (
    <div
      data-type="InstagramPostsComponent"
      data-posts-count={actualPostCount}
      className={`flex bg-[var(--instagram-bg-color)] ${mainContainerClass.replace("flex ", "")}`}
      style={
        {
          "--instagram-text-color": finalConfig.textColor,
          "--instagram-border-color": finalConfig.borderColor,
          "--instagram-bg-color": finalConfig.bgColor,
          "--instagram-icon-color": finalConfig.iconColor,
          "--instagram-caption-bg-color":
            finalConfig.captionBgColor || "rgba(0, 0, 0, 0.8)",
          ...ownRadiusStyle(finalConfig.rounded, lookSystem),
        } as React.CSSProperties
      }
    >
      <div
        className={gridContainerClass}
        style={{
          borderWidth: finalConfig.borderColor ? "1px" : "0px",
          borderStyle: "solid",
        }}
      >
        {/* Instagram Grid - Responsive Grid Layout */}
        <div className={`grid gap-3 ${gridConfig.cols}`}>
          {posts.map((post, index) => (
            <div key={post.id} className="flex justify-center">
              {renderInstagramPost(post, index)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InstagramPostsComponent;
